import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type WebsiteRole = "admin" | "user" | "viewer";

export interface MonitoredWebsite {
  id: string;
  domain: string;
  label: string | null;
  logo_url: string | null;
  sla_business_days: number;
  sla_warning_days: number;
  scan_viewport: "desktop" | "mobile" | "both";
  role: WebsiteRole;
}

interface SelectedWebsiteContextType {
  websites: MonitoredWebsite[];
  selectedWebsite: MonitoredWebsite | null;
  setSelectedWebsiteId: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SelectedWebsiteContext = createContext<SelectedWebsiteContextType>({
  websites: [],
  selectedWebsite: null,
  setSelectedWebsiteId: () => {},
  loading: true,
  refresh: async () => {},
});

export const useSelectedWebsite = () => useContext(SelectedWebsiteContext);

export const SelectedWebsiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [websites, setWebsites] = useState<MonitoredWebsite[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const selectedIdRef = React.useRef(selectedId);

  // Keep ref in sync
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const fetchWebsites = React.useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    const [{ data: memberships }, { data: ownedWebsites }] = await Promise.all([
      supabase
        .from("website_members")
        .select("website_id, role")
        .eq("user_id", user.id),
      supabase
        .from("monitored_websites")
        .select("id, domain, label, logo_url, sla_business_days, sla_warning_days, scan_viewport")
        .eq("user_id", user.id)
        .order("created_at"),
    ]);

    const roleMap: Record<string, WebsiteRole> = {};
    for (const m of memberships ?? []) {
      roleMap[m.website_id] = m.role as WebsiteRole;
    }

    const ownedSites = (ownedWebsites ?? []).map((website) => ({
      ...website,
      role: roleMap[website.id] || "admin",
    })) as MonitoredWebsite[];

    const membershipWebsiteIds = (memberships ?? [])
      .map((membership) => membership.website_id)
      .filter((id) => !ownedSites.some((website) => website.id === id));

    if (ownedSites.length === 0 && membershipWebsiteIds.length === 0) {
      setWebsites([]);
      setLoading(false);
      return;
    }

    let sharedSites: MonitoredWebsite[] = [];

    if (membershipWebsiteIds.length > 0) {
      const { data } = await supabase
        .from("monitored_websites")
        .select("id, domain, label, logo_url, sla_business_days, sla_warning_days, scan_viewport")
        .in("id", membershipWebsiteIds)
        .order("created_at");

      sharedSites = (data ?? []).map((website) => ({
        ...website,
        role: roleMap[website.id] || ("viewer" as WebsiteRole),
      })) as MonitoredWebsite[];
    }

    const sites = [...ownedSites, ...sharedSites];

    setWebsites(sites);
    // Use ref to avoid stale closure
    if (sites.length > 0 && !sites.find(s => s.id === selectedIdRef.current)) {
      setSelectedId(sites[0].id);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchWebsites(); }, []);

  const selectedWebsite = websites.find(w => w.id === selectedId) ?? null;

  return (
    <SelectedWebsiteContext.Provider value={{
      websites,
      selectedWebsite,
      setSelectedWebsiteId: setSelectedId,
      loading,
      refresh: fetchWebsites,
    }}>
      {children}
    </SelectedWebsiteContext.Provider>
  );
};
